import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/**
 * Edición del umbral de markup y del intervalo de reaparición del aviso.
 *
 * Los dos valores viven en la tabla de parámetros del backend, no en el
 * bundle: se cambian en caliente, sin desplegar. En producción el backend
 * rechaza la escritura si el correo no es el del encargado de la tasa — este
 * modal solo evita ofrecer un botón que iba a fallar.
 */
@Component({
  selector: 'app-edit-markup-umbral-modal',
  template: `
    <div style="padding: 26px 26px 22px; min-width: 320px;">
      <div class="flex items-center gap-2" style="margin-bottom: 6px;">
        <mat-icon style="color: #1C593D;">tune</mat-icon>
        <h2 style="font-size: 1.15rem; font-weight: 800; color: #111827; margin: 0;">
          Umbral de markup
        </h2>
      </div>
      <p style="font-size: 0.85rem; color: #6b7280; margin: 0 0 20px; line-height: 1.5;">
        Cuánto puede despegarse la tasa Aguacate del spot antes de que se
        avise. Se aplica de inmediato, sin desplegar.
      </p>

      <form [formGroup]="form">
        <label style="display: block; font-size: 0.75rem; font-weight: 700; color: #374151; margin-bottom: 6px;">
          Ratio máximo aguacate / spot
        </label>
        <input
          type="number"
          step="0.001"
          formControlName="maxRatio"
          style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 10px; font-family: monospace; font-size: 1rem; color: #111827;"
        />
        <p style="font-size: 0.78rem; color: #6b7280; margin: 6px 0 0;">
          Equivale a un markup máximo de
          <b style="font-family: monospace; color: #1C593D;">{{ markupMaximoPct }}%</b>
        </p>
        <p *ngIf="form.controls['maxRatio'].invalid && form.controls['maxRatio'].touched"
          style="font-size: 0.78rem; color: #b91c1c; margin: 6px 0 0;">
          Tiene que ser mayor que 1. Un umbral en 1 o menos dejaría el aviso encendido siempre.
        </p>

        <label style="display: block; font-size: 0.75rem; font-weight: 700; color: #374151; margin: 18px 0 6px;">
          El aviso reaparece a los (segundos)
        </label>
        <input
          type="number"
          step="30"
          formControlName="reaparicionSeg"
          style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 10px; font-family: monospace; font-size: 1rem; color: #111827;"
        />
        <p style="font-size: 0.78rem; color: #6b7280; margin: 6px 0 0;">
          {{ minutosReaparicion }} minutos después de que un operador lo reconoce.
        </p>
        <p *ngIf="form.controls['reaparicionSeg'].invalid && form.controls['reaparicionSeg'].touched"
          style="font-size: 0.78rem; color: #b91c1c; margin: 6px 0 0;">
          Mínimo 30 segundos.
        </p>
      </form>

      <div class="flex gap-2 modal-botones" style="margin-top: 24px;">
        <button
          type="button"
          (click)="dialogRef.close()"
          style="flex: 1; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 10px; padding: 12px; font-size: 0.9rem; font-weight: 600; cursor: pointer;"
        >
          Cancelar
        </button>
        <button
          type="button"
          (click)="guardar()"
          [disabled]="form.invalid"
          [style.opacity]="form.invalid ? '0.5' : '1'"
          [style.cursor]="form.invalid ? 'not-allowed' : 'pointer'"
          style="flex: 1; background: #1C593D; color: white; border: none; border-radius: 10px; padding: 12px; font-size: 0.9rem; font-weight: 700;"
        >
          Guardar
        </button>
      </div>
    </div>
  `,
})
export class EditMarkupUmbralModalComponent {
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<EditMarkupUmbralModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { maxRatio: number; reaparicionSeg: number },
  ) {
    this.form = this.fb.group({
      // 1.000001 y no 1: el backend valida lo mismo, y así el error se ve acá
      // en vez de volver como un 400 sin explicación.
      maxRatio: [
        data.maxRatio,
        [Validators.required, Validators.min(1.000001)],
      ],
      reaparicionSeg: [
        data.reaparicionSeg,
        [Validators.required, Validators.min(30)],
      ],
    });
  }

  get markupMaximoPct(): string {
    const ratio = Number(this.form.controls['maxRatio'].value);
    if (!isFinite(ratio)) return '--';
    return ((ratio - 1) * 100).toFixed(3);
  }

  get minutosReaparicion(): string {
    const seg = Number(this.form.controls['reaparicionSeg'].value);
    if (!isFinite(seg) || seg <= 0) return '--';
    return (seg / 60).toFixed(1);
  }

  guardar(): void {
    if (this.form.invalid) return;
    this.dialogRef.close({
      maxRatio: Number(this.form.value.maxRatio),
      reaparicionSeg: Math.round(Number(this.form.value.reaparicionSeg)),
    });
  }
}
